<template>
	<div class="refer-page-container">
		<div class="refer-search-container">
			<input class="refer-search-input" type="text" v-model="this.searchKeyword"
			       placeholder="输入推荐人 / 被推荐人邮箱或推荐码搜索" @keyup.enter="onSearchClicked">
			<div class="header-btn refer-search-btn" @click="onSearchClicked">搜索</div>
			<div class="header-btn refer-search-btn" @click="onResetSearchClicked">重置</div>
		</div>

		<div class="btn-container">
			<div class="refer-page-title">{{ this.getListTitle() }}</div>

			<a-pagination v-model:current="this.page" simple :pageSize="this.pageSize"
			              :total="this.totalCount" @change="onPageChanged"
			              :showSizeChanger="false"/>

			<div class="header-btn refer-search-btn" @click="onRefreshBtnClicked">刷新</div>
		</div>

		<!-- 创建按钮单独一行放在分页下面：它是写操作，混在翻页那一行里容易误点 -->
		<div class="refer-create-container">
			<div class="header-btn refer-create-btn" @click="onCreateClicked">创建推荐</div>
		</div>

		<div class="cs-main-container">
			<div class="list-container">
				<div class="refer-empty" v-if="this.isLoaded === true && this.referList.length === 0">
					{{ this.isSearch === true ? '没有搜到匹配的推荐记录' : '还没有推荐记录' }}
				</div>

				<template v-for="item in this.referList" :key="item.recordId || item.referToEmail">
					<div class="item-container refer-row">
						<!-- 推荐人 → 被推荐人 放在第一行：这条记录要回答的就是"谁拉来的谁" -->
						<div class="refer-relation-line">
							<span class="refer-email-text">{{ item.referByEmail || '未知推荐人' }}</span>
							<img src="../images/copy_light.svg" alt="复制" class="copy-text-btn"
							     title="复制推荐人邮箱" v-if="item.referByEmail"
							     @click="onCopyText(item.referByEmail, '推荐人邮箱')">
							<span class="refer-arrow">→</span>
							<span class="refer-email-text">{{ item.referToEmail || '未知被推荐人' }}</span>
							<img src="../images/copy_light.svg" alt="复制" class="copy-text-btn"
							     title="复制被推荐人邮箱" v-if="item.referToEmail"
							     @click="onCopyText(item.referToEmail, '被推荐人邮箱')">
							<!-- 机场是另一条业务线，混在一个列表里必须能一眼分开 -->
							<span class="refer-tag is-airport" v-if="item.isAirport === true">机场</span>
							<span class="refer-tag" v-if="item.referTyp">{{ item.referTyp }}</span>
						</div>

						<div class="refer-field-line">
							<span class="refer-field-label">推荐码</span>
							<span class="refer-code-text">{{ item.referCode || '无' }}</span>
							<img src="../images/copy_light.svg" alt="复制" class="copy-text-btn"
							     title="复制推荐码" v-if="item.referCode"
							     @click="onCopyText(item.referCode, '推荐码')">
						</div>

						<div class="refer-field-line">
							<span class="refer-field-label">创建时间</span>
							<span>{{ this.getCreateTimeText(item) }}</span>
						</div>
					</div>
				</template>
			</div>
		</div>

		<!-- 创建推荐关系的弹窗。整页只放一个 -->
		<a-modal v-model:open="this.isCreateModalOpen" title="创建推荐关系"
		         ok-text="确认创建" cancel-text="取消"
		         :confirm-loading="this.isCreating"
		         @ok="onCreateConfirm">
			<div class="create-modal-body">
				<div class="create-modal-line">推荐人邮箱</div>
				<!-- 用原生 input：App.vue 里那条全局的 input {padding: 0 !important}
				     会把 antd 的输入框压扁，自己写的这个能在下面的 scoped 样式里改回来 -->
				<input class="create-modal-input" type="text" v-model="this.createReferByEmail"
				       placeholder="拉人的那个账号">
				<div class="create-modal-line">被推荐人邮箱</div>
				<input class="create-modal-input" type="text" v-model="this.createReferToEmail"
				       placeholder="被拉来的那个账号" @keyup.enter="onCreateConfirm">
				<!-- 说清两条约束，省得填完才被后端打回来 -->
				<div class="create-modal-tip">
					两个账号都必须已经注册；一个账号只能有一条推荐关系，已有关系的会先问一句再覆盖。
				</div>
			</div>
		</a-modal>

		<!-- 被推荐人已经有关系时的二次确认。单开一个弹窗而不是用 confirm()：
		     要把原推荐人和建立时间摆出来，这一下删的是别人的分红来源 -->
		<a-modal v-model:open="this.isConflictModalOpen" title="已有推荐关系"
		         ok-text="删除原关系并创建" cancel-text="取消"
		         :ok-button-props="{danger: true}"
		         :confirm-loading="this.isCreating"
		         @ok="onConflictConfirm">
			<div class="create-modal-body">
				<div class="create-modal-line">
					{{ this.createReferToEmail }} 已经被
					<span class="conflict-email">{{ this.conflictReferByEmail || '未知账号' }}</span> 推荐过了。
				</div>
				<div class="create-modal-line" v-if="this.conflictCreateTimeString">
					原关系建立于 {{ this.conflictCreateTimeString }}
				</div>
				<div class="create-modal-tip">
					确定后会先删掉原有的推荐关系，再建立
					{{ this.createReferByEmail }} → {{ this.createReferToEmail }}。
					已经结算出去的分红不会跟着改，覆盖只对之后的订单生效。
				</div>
			</div>
		</a-modal>
	</div>
</template>

<script>
import {copyToClipboard, hideLoading, postRequestAsync, showLoading} from "@/utils/methods";
import moment from "moment-timezone";

export default {
	name: "ReferencePage",
	data() {
		return {
			referList: [],
			searchKeyword: '',
			// 当前列表是搜索结果还是全量列表，空列表的文案不一样
			isSearch: false,
			page: 1,
			pageSize: 20,
			totalCount: 0,
			// 区分"还没查"和"查完是空的"，否则一进页面就会闪一下空列表提示
			isLoaded: false,
			isCreateModalOpen: false,
			createReferByEmail: '',
			createReferToEmail: '',
			isCreating: false,
			// 被推荐人已有关系时的二次确认：原推荐人和建立时间由后端一起带回来，
			// 要在确认框里摆给人看，不能只说一句"已存在"
			isConflictModalOpen: false,
			conflictReferByEmail: '',
			conflictCreateTimeString: ''
		}
	},
	async mounted() {
		await this.getReferenceList()
	},
	methods: {
		async getReferenceList(isManual = false) {
			showLoading()
			const result = await postRequestAsync('admin/reference/getReferenceList', {
				keyword: this.searchKeyword.trim(),
				page: this.page
			})
			hideLoading()

			if (result?.code === 200) {
				this.referList = result?.msg?.list || []
				this.isSearch = result?.msg?.isSearch === true
				this.totalCount = result?.msg?.totalCount || 0
				this.pageSize = result?.msg?.pageSize || this.pageSize
				this.isLoaded = true
				if (isManual === true) {
					this.$message.success('刷新成功')
				}
			} else {
				this.$message.error(`获取推荐记录失败${result?.code ? `（${result.code}）` : ''}`)
			}
		},
		async onRefreshBtnClicked() {
			await this.getReferenceList(true)
		},
		getListTitle() {
			if (this.isSearch === true) {
				return `搜索结果（${this.totalCount}）`
			}
			return `推荐记录（${this.totalCount}）`
		},
		async onPageChanged(page) {
			this.page = page
			await this.getReferenceList()
		},
		async onSearchClicked() {
			// 换了关键字，之前那一页的页码就没意义了
			this.page = 1
			await this.getReferenceList()
		},
		async onResetSearchClicked() {
			if (!this.searchKeyword) {
				return
			}
			this.searchKeyword = ''
			this.page = 1
			await this.getReferenceList()
		},
		/**
		 * 创建时间。库里 createTimeString 是写记录时按东八区格式化好的，直接用；
		 * 早期有几条只写了时间戳，退回自己格式化一遍，总比显示"无"强。
		 */
		getCreateTimeText(item) {
			if (item?.createTimeString) {
				return item.createTimeString
			}
			const timestamp = parseInt(item?.createTimestamp)
			if (!timestamp) {
				return '无'
			}
			return moment(timestamp * 1000).tz('Asia/Shanghai').format('YYYY-MM-DD HH:mm:ss')
		},
		onCreateClicked() {
			// 每次打开都从空开始，免得把上一次填的邮箱又提交一遍
			this.createReferByEmail = ''
			this.createReferToEmail = ''
			this.isCreateModalOpen = true
		},
		async onCreateConfirm() {
			await this.submitCreate(false)
		},
		/**
		 * 二次确认里点了"删除原关系并创建"：同一份表单再发一次，这次带 force。
		 * 覆盖与否由后端一次做完（删旧 + 建新），前端不单独调删除接口——
		 * 分两次请求的话中间失败就会留下一个谁都没推荐的人。
		 */
		async onConflictConfirm() {
			await this.submitCreate(true)
		},
		/**
		 * 补一条推荐关系。
		 *
		 * 两个 userId 由后端按邮箱回库查，这里只传邮箱：下游分红认的是 userId，
		 * 前端这边根本没有那个字段，拼错了这条记录就是死的。
		 *
		 * @param force 被推荐人已有关系时是否删掉原关系再建。首次提交一律 false，
		 *              等后端退回 305、人在确认框里点了确定，才用 true 重发。
		 */
		async submitCreate(force = false) {
			if (this.isCreating) {
				return
			}

			const referByEmail = this.createReferByEmail.trim()
			const referToEmail = this.createReferToEmail.trim()
			if (!referByEmail || !referToEmail) {
				this.$message.error('两个邮箱都要填')
				return
			}
			if (referByEmail.toLowerCase() === referToEmail.toLowerCase()) {
				this.$message.error('推荐人和被推荐人不能是同一个账号')
				return
			}

			this.isCreating = true
			try {
				const result = await postRequestAsync('admin/reference/createReference', {
					referByEmail: referByEmail,
					referToEmail: referToEmail,
					force: force === true
				})
				if (result?.code === 200) {
					const replacedCount = result?.msg?.replacedCount || 0
					this.$message.success(replacedCount > 0
						? `已删除原有推荐关系并创建新的`
						: '已创建推荐关系')
					this.isConflictModalOpen = false
					this.isCreateModalOpen = false
					this.createReferByEmail = ''
					this.createReferToEmail = ''
					// 新记录时间最新，回第一页才看得到它
					this.page = 1
					await this.getReferenceList()
				} else if (result?.code === 302) {
					this.$message.error('两个邮箱都要填')
				} else if (result?.code === 303) {
					this.$message.error('用户表里找不到推荐人这个账号')
				} else if (result?.code === 304) {
					this.$message.error('用户表里找不到被推荐人这个账号')
				} else if (result?.code === 305) {
					// 已经有关系了：把原推荐人和建立时间摆进确认框，
					// 点确定才带 force 重发一次，这一下会删掉原来那条
					this.conflictReferByEmail = result?.msg?.referByEmail || ''
					this.conflictCreateTimeString = result?.msg?.createTimeString || ''
					this.isConflictModalOpen = true
				} else if (result?.code === 306) {
					this.$message.error('推荐人和被推荐人不能是同一个账号')
				} else {
					this.$message.error(`创建失败${result?.code ? `（${result.code}）` : ''}`)
				}
			} finally {
				this.isCreating = false
			}
		},
		/**
		 * 复制任意一段文本。label 只用来拼提示语（"推荐码已复制"），
		 * 复制失败一律提示手动选中——浏览器在非安全上下文里会直接拒绝写剪贴板。
		 */
		async onCopyText(text, label) {
			if (!text) {
				return
			}
			const ok = await copyToClipboard(text)
			if (ok) {
				this.$message.success(`${label}已复制`)
			} else {
				this.$message.error('复制失败，请手动选中复制')
			}
		}
	}
}
</script>

<style scoped>
.refer-page-container {
	min-height: 100vh;
	position: relative;
	background-color: #191123;
	overflow-x: hidden;
	width: 100vw;
	padding: 1em 0;
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
}

.refer-search-container {
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: center;
	width: 100%;
	box-sizing: border-box;
	padding: 0 1em;
	gap: 0.6em;
}

.refer-search-input {
	flex: 1;
	/* flex 子项默认 min-width:auto，输入框会被内容撑开把按钮挤出屏幕 */
	min-width: 0;
	max-width: 30em;
	color: #E6D5B8;
	background-color: transparent;
	border: 1px solid rgba(230, 213, 184, 0.6);
	border-radius: 0.2em;
	/* App.vue 里有全局的 input {padding: 0 !important}，不加 !important 撑不开 */
	padding: 0.2em 0.8em !important;
	font-size: 1.2em;
	line-height: 1.2em;
	outline: none;
}

.refer-search-input:focus {
	border-color: #FF8900;
}

.refer-search-input::placeholder {
	color: rgba(230, 213, 184, 0.4);
}

/* 标题 | 分页 | 刷新 三段，窄屏放不下时整行换行 */
.btn-container {
	flex-wrap: wrap;
	row-gap: 0.4em;
	padding: 0 1em;
	margin-bottom: 0.4em;
}

/* 分页 / 搜索框 / 按钮统一对齐工单页的类目切换按钮：
   同为 font-size 1.2em，高度 = 行高 1.2em + 上下各 0.2em 内边距 + 1px 边框 */
.btn-container :deep(.ant-pagination) {
	font-size: 1.2em;
}

.btn-container :deep(.ant-pagination-simple .ant-pagination-prev),
.btn-container :deep(.ant-pagination-simple .ant-pagination-next),
.btn-container :deep(.ant-pagination-simple .ant-pagination-simple-pager) {
	height: calc(1.6em + 2px);
	line-height: calc(1.6em + 2px);
	min-width: calc(1.6em + 2px);
	margin-inline-end: 0.4em;
}

/* 翻页组最后一项的尾随间距要去掉，否则这一行的右边比其它行少 0.4em，看着不齐 */
.btn-container :deep(.ant-pagination-simple .ant-pagination-next) {
	margin-inline-end: 0;
}

.btn-container :deep(.ant-pagination-simple .ant-pagination-item-link) {
	display: flex;
	align-items: center;
	justify-content: center;
	height: calc(1.6em + 2px);
	min-width: calc(1.6em + 2px);
	border-radius: 0.2em;
	font-size: 1em;
}

.btn-container :deep(.ant-pagination-simple .ant-pagination-simple-pager input) {
	height: calc(1.6em + 2px);
	/* 页码框不画边框，只留数字；宽度收窄到刚好放下页码。
	   App.vue 里有全局的 .ant-pagination.ant-pagination-simple input {border: 1px ... !important}，
	   不加 !important 盖不掉 */
	border: none !important;
	box-shadow: none;
	background-color: transparent;
	min-width: 2em;
	border-radius: 0.2em;
	font-size: 1em;
	line-height: 1.2em;
	text-align: center;
	/* App.vue 里有全局的 input {padding: 0 !important}，不加 !important 撑不开 */
	padding: 0 0.2em !important;
	margin-right: 0.4em;
}

/* 全局 .header-btn 比搜索框矮一截，这里统一成和输入框一样的高度 */
.refer-search-btn {
	font-size: 1.2em;
	line-height: 1.2em;
	padding: 0.2em 1em;
	border-radius: 0.2em;
	white-space: nowrap;
}

.refer-page-title {
	color: #E6D5B8;
	font-size: 1.2em;
	padding: 0.8em 0;
}

/* 创建按钮靠左起头，和下面列表卡片的左边缘对齐 */
.refer-create-container {
	display: flex;
	flex-direction: row;
	align-items: center;
	width: 100%;
	box-sizing: border-box;
	padding: 0 1em;
	margin-bottom: 1em;
}

/* 实心橙：这是整页唯一的写操作，比刷新/搜索那几个描边按钮更实 */
.refer-create-btn {
	color: #191123;
	background-color: #FF8900;
	border-color: #FF8900;
	font-size: 1.1em;
	line-height: 1.2em;
	padding: 0.3em 1.2em;
	border-radius: 0.2em;
	white-space: nowrap;
}

@media only screen and (max-width: 900px) {
	.refer-search-container {
		padding: 0 1.2em;
		gap: 0.5em;
	}

	.btn-container,
	.refer-create-container {
		padding: 0 1.2em;
	}

	.refer-search-btn {
		padding: 0.2em 0.7em;
	}
}

.refer-empty {
	color: rgba(230, 213, 184, 0.6);
	padding: 2em 0;
	text-align: center;
	width: 100%;
}

/* item-container 是全局样式，这里只补内边距；#app 上有全局的 text-align: center，
   不显式改回来每一行都会居中 */
.refer-row {
	padding: 0.8em;
	cursor: default;
	text-align: left;
	width: 100%;
	/* 不加 min-width:0，长邮箱会把卡片撑出屏幕 */
	min-width: 0;
}

.refer-relation-line {
	display: flex;
	flex-direction: row;
	align-items: center;
	flex-wrap: wrap;
	gap: 0.4em;
	min-width: 0;
	margin-bottom: 0.3em;
}

.refer-email-text {
	color: #E6D5B8;
	font-size: 1.1em;
	word-break: break-all;
}

/* 箭头是这一行的方向指示，比两侧的邮箱淡一档，不该抢读 */
.refer-arrow {
	color: #FF8900;
	font-size: 1.1em;
}

.refer-tag {
	color: rgba(230, 213, 184, 0.6);
	font-size: 0.85em;
	padding: 0.1em 0.5em;
	border-radius: 0.3em;
	border: 1px solid currentColor;
	white-space: nowrap;
}

.refer-tag.is-airport {
	color: #52c41a;
}

.refer-field-line {
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 0.4em;
	color: rgba(230, 213, 184, 0.85);
	font-size: 0.95em;
	margin-top: 0.2em;
	min-width: 0;
	word-break: break-all;
}

.refer-field-label {
	color: rgba(230, 213, 184, 0.5);
	/* 固定宽度让右边的值竖着对齐 */
	flex: 0 0 5em;
}

.refer-code-text {
	word-break: break-all;
}

.copy-text-btn {
	width: 1em;
	height: 1em;
	flex: 0 0 auto;
	cursor: pointer;
	opacity: 0.7;
}

.copy-text-btn:hover {
	opacity: 1;
}

.create-modal-body {
	text-align: left;
}

.create-modal-line {
	margin-bottom: 0.2em;
}

.create-modal-input {
	width: 100%;
	box-sizing: border-box;
	border: 1px solid #d9d9d9;
	border-radius: 0.3em;
	/* App.vue 里有全局的 input {padding: 0 !important}，不加 !important 撑不开 */
	padding: 0.5em 0.8em !important;
	font-size: 1em;
	line-height: 1.4em;
	outline: none;
	margin-bottom: 0.8em;
}

.create-modal-input:focus {
	border-color: #FF8900;
}

/* 原推荐人是这个确认框里最关键的一条信息：删的就是他那条关系 */
.conflict-email {
	color: #d4380d;
	word-break: break-all;
}

.create-modal-tip {
	color: rgba(0, 0, 0, 0.45);
	font-size: 0.9em;
	line-height: 1.4em;
}
</style>
